import type { POIType } from '@/pages/itinerary-builder/utils/waypointTypeStyles'

/**
 * Generate a smart waypoint name based on POI type and custom label
 * @param poiType - The POI type (null for Route Stop)
 * @param customLabel - Custom label for custom POI types (e.g., "Lunch Stop", "Overlook")
 * @param existingNames - Optional array of existing waypoint names to avoid duplicates
 * @returns A user-friendly waypoint name
 */
export function generateWaypointName(
  poiType: POIType,
  customLabel?: string | null,
  existingNames: string[] = []
): string {
  // Handle custom types with custom labels
  if (poiType === 'custom' && customLabel) {
    // For custom labels like "Lunch Stop" or "Overlook", use them directly
    // Add a number if there are duplicates
    const baseName = customLabel
    return getUniqueName(baseName, existingNames)
  }

  // Map POI types to base names
  const typeNameMap: Record<string, string> = {
    campsite: 'Campsite',
    meetup_spot: 'Meetup Spot',
    viewpoint: 'Photo Spot',
    trailhead: 'Hike',
    gas: 'Gas Station',
    water: 'Water Source',
  }

  // Get base name for the POI type
  const baseName = poiType ? typeNameMap[poiType] || 'Waypoint' : 'Route Stop'

  // Add a number if there are duplicates
  return getUniqueName(baseName, existingNames)
}

/**
 * Get a unique name by appending a number if duplicates exist
 */
function getUniqueName(baseName: string, existingNames: string[]): string {
  // Check if base name already exists
  const exactMatch = existingNames.includes(baseName)
  if (!exactMatch) {
    return baseName
  }

  // Find the highest number suffix
  let maxNumber = 0
  const namePattern = new RegExp(`^${escapeRegex(baseName)}\\s+(\\d+)$`)
  
  existingNames.forEach(name => {
    const match = name.match(namePattern)
    if (match) {
      const number = parseInt(match[1], 10)
      if (number > maxNumber) {
        maxNumber = number
      }
    }
  })

  // Return name with next number
  return `${baseName} ${maxNumber + 1}`
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
